import { memo, useState, useEffect, useMemo } from 'react';

import { v4 as uuid } from 'uuid';

import MarkerConfigurationInputs from './MarkerConfigurationInputs';
import MarkerDeltaDisplay from './MarkerDeltaDisplay';

import { Button, Grid } from '@mui/material';

import { setTemporaryMarkerDeltas } from '../snapshots/snapshotsSlice';
import {
  selectTraceDataByAnalyzerId,
  updateAnalyzerProperties,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import {
  selectMarkersBySnapshotId,
  markerMoved,
  trendlineModeToggled,
  clearClientsideMarkers,
  selectedMarkerChanged,
} from '../markers/markersSlice';

import {
  freqValue,
  freqUnit,
  getIndexFromSnapshotFrequency,
  getFrequencyArray,
  getCenterWithLo,
} from '../../utils/FreqUtils';

import {
  insertSnapshotMarkerDelta,
  clearSnapshotMarkerDeltas,
} from '../../api/snapshotApi';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { MarkerIndex, ParsedMarker, CreateSnapshotMarkerDelta } from './types';

import { getCenterWithoutLo } from '../../utils/FreqUtils';
import { useIsViewer } from '../auth/hooks/useIsViewer';
import { useIsSnapshotLocked } from '../analyzers/hooks/useIsSnapshotLocked';
import { TraceHistory, Snapshot } from 'types';
import { parseTraceFromBuffer } from 'utils/traceUtils';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useMarkers } from 'features/markers/api/getMarkers';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { useCreateMarker } from 'features/markers/api/createMarker';
import { useClearMarkers } from 'features/markers/api/clearMarkers';
import { useClearTrendlineMarkers } from 'features/markers/api/clearTrendlineMarkers';
import TrendlineMarkerConfigurationInputs from './TrendlineMarkerConfigurationInputs';
import { queryClient } from 'lib/react-query';
import {
  convertFrequency,
  // @ts-ignore
} from 'hertzbel';
import { debounce } from 'features/spectrumChart/utils/debounce';

interface Props {
  handleClose: () => void;
  selectedAnalyzerId: number;
  selectedSnapshotId: number;
  historicalSnapshot: TraceHistory | Snapshot;
  isHistoricalMode?: boolean;
  inDrawingMode?: boolean;
  hasChanges: boolean;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

const MarkerConfigurationDialog = memo(
  ({
    handleClose,
    selectedAnalyzerId,
    selectedSnapshotId,
    historicalSnapshot,
    isHistoricalMode,
    inDrawingMode,
    hasChanges,
    setHasChanges,
  }: Props) => {
    const { user } = useAppSelector((state) => state.auth);
    const isViewer = useIsViewer();
    const trendlineMode = useAppSelector(
      (state) => state.markers.trendlineMode
    );

    const dispatch = useAppDispatch();

    const { data: selectedAnalyzer } = useAnalyzer({
      analyzerId: selectedAnalyzerId,
    });
    const { data: selectedSnapshot } = useSnapshot({
      snapshotId: selectedSnapshotId,
    });
    const updateSnapshotMutation = useUpdateSnapshot({});
    const createMarkerMutation = useCreateMarker({
      snapshotId: selectedSnapshotId,
    });
    const clearMarkersMutation = useClearMarkers({
      snapshotId: selectedSnapshotId,
    });
    const clearTrendlineMarkers = useClearTrendlineMarkers({
      snapshotId: selectedSnapshotId,
    });

    const isSnapshotLocked = useIsSnapshotLocked(selectedSnapshot);

    let data = useAppSelector((state) =>
      selectTraceDataByAnalyzerId(state, selectedSnapshotId)
    );

    const snapshotFrequencyArray = useMemo<string[]>(() => {
      if (!selectedSnapshot) return [];
      return getFrequencyArray(
        selectedSnapshot.center,
        selectedSnapshot.span,
        data?.length,
        selectedSnapshot.lo,
        selectedSnapshot.lo_mode
      );
    }, [data?.length, selectedSnapshot]);

    const { data: savedMarkers } = useMarkers({
      snapshotId: selectedSnapshotId,
    });
    const selectCurrentMarkers = useMemo(selectMarkersBySnapshotId, []);
    const tempMarkers = useAppSelector((state) =>
      selectCurrentMarkers(state, selectedSnapshotId)
    );

    const [currentMarkers, setCurrentMarkers] = useState<ParsedMarker[]>([]);
    const [currentMarkerDeltas, setCurrentMarkerDeltas] = useState<
      CreateSnapshotMarkerDelta[]
    >(selectedSnapshot?.snapshot_marker_delta || []);
    const [markerAmplitudes, setMarkerAmplitudes] = useState<number[]>([]);
    const [focusedMarkerInput, setFocusedMarkerInput] = useState<null | number>(
      null
    );

    // null out focusedMarkerInput when a user starts to draw a marker or mask to avoid
    //  unintentially moving the selected marker when the user is done drawing
    useEffect(() => {

      if (inDrawingMode === false)
        setFocusedMarkerInput(null);
        
    }, [inDrawingMode]);

  const enablePeak = () => {
    return (focusedMarkerInput || focusedMarkerInput === 0) ? false : true;
  }

    useEffect(() => {
      if (!tempMarkers || tempMarkers.length === 0) return;
      setHasChanges(true);
      //eslint-disable-next-line
    }, [tempMarkers]);

    useEffect(() => {
      const newData =
        isHistoricalMode && 'trace' in historicalSnapshot
          ? parseTraceFromBuffer(historicalSnapshot.trace)
          : data;

      if (currentMarkers.length === 0 || !newData || newData.length === 0)
        return;

      const amplitudes: number[] = [];
      for (const [key] of Object.entries(currentMarkers)) {
        if (currentMarkers[Number(key)]?.index === undefined) continue;
        amplitudes[currentMarkers[Number(key)].index] = Number(
          newData[currentMarkers[Number(key)].x_pos]
        );
      }
      setMarkerAmplitudes(amplitudes);
    }, [data, currentMarkers, isHistoricalMode, historicalSnapshot]);

    useEffect(() => {
      const markers = [
        ...(savedMarkers ? savedMarkers : []),
        ...(tempMarkers ? tempMarkers : []),
      ];
      if (markers === undefined) return;
      const markersWithFrequencies: ParsedMarker[] = [];

      for (let i = 0; i < markers.length; i++) {
        let frequency = snapshotFrequencyArray[markers[i].x_pos];
        let frequencyValue = freqValue(frequency);
        let frequencyUnit = freqUnit(frequency);
        const currentMarker = currentMarkers[markers[i].index];

        //If we have a current adjusted marker, re-use its unit. And if the x_pos is invalid, just use current input
        if (currentMarker) {
          if (!frequency) {
            frequency = `${currentMarker?.x_pos_frequency_value} ${currentMarker.x_pos_frequency_unit}`;
            frequencyValue =
              currentMarker.x_pos_frequency_value === 0
                ? ''
                : currentMarker.x_pos_frequency_value.toString();
          }

          if (
            currentMarker.x_pos_frequency_unit !== frequencyUnit &&
            frequencyValue.length > 0
          ) {
            const currentUnit = freqUnit(snapshotFrequencyArray[0]);

            const adjustedFreq = convertFrequency(
              frequency,
              currentMarker?.x_pos_frequency_unit || currentUnit
            );
            if (adjustedFreq)
              frequencyValue = (+adjustedFreq.split(' ')[0]).toString();
          }

          frequencyUnit = currentMarker.x_pos_frequency_unit;
        }

        const parsedMarker: ParsedMarker = {
          ...markers[i],
          x_pos_frequency_value: frequencyValue,
          x_pos_frequency_unit: frequencyUnit,
        };

        markersWithFrequencies[markers[i].index] = parsedMarker;
      }
      setCurrentMarkers(markersWithFrequencies);
      //eslint-disable-next-line
    }, [tempMarkers, savedMarkers, snapshotFrequencyArray]);

    const handleMarkerInputClicked = (markerIndex: MarkerIndex) => {
      setFocusedMarkerInput(markerIndex);
      dispatch(selectedMarkerChanged(markerIndex));
    };

    const handleMarkerDeltaChanged = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number
    ) => {
      const { name, value } = event.target;
      let currentMarkerDeltasCopy = [...currentMarkerDeltas];
      let changedMarkerDeltaIndex = currentMarkerDeltas.findIndex(
        (delta) => delta.marker2_index === index
      );
      let changedMarkerDelta = currentMarkerDeltasCopy[changedMarkerDeltaIndex];
      let computedValue;

      if (Number(value) === 0 || isNaN(Number(value) * 1000)) {
        computedValue = value;
      } else {
        computedValue = Number(value) * 1000;
      }

      if (changedMarkerDelta) {
        changedMarkerDelta = {
          ...changedMarkerDelta,
          [name]: computedValue,
        };
        currentMarkerDeltasCopy[changedMarkerDeltaIndex] = changedMarkerDelta;
      } else {
        let newMarkerDelta = {
          snapshot_id: selectedSnapshotId,
          upperbound: 0,
          lowerbound: 0,
          marker1_index: 0,
          marker2_index: index,
        };

        newMarkerDelta = {
          ...newMarkerDelta,
          [name]: computedValue,
        };

        currentMarkerDeltasCopy.push(newMarkerDelta);
      }
      setHasChanges(true);
      setCurrentMarkerDeltas(currentMarkerDeltasCopy);
    };

    const handleMarkerNameChanged = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: MarkerIndex
    ) => {
      const updatedMarkers = currentMarkers.map((marker) => {
        if (marker.index === index) {
          return { ...marker, name: event.target.value };
        }

        return marker;
      });

      setCurrentMarkers(updatedMarkers);
      setHasChanges(true);
    };

    const debouncedMarkerMoved = debounce(
      (updatedMarker: any) => dispatch(markerMoved(updatedMarker)),
      1000
    );

    const handleMarkerValueChanged = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: MarkerIndex
    ) => {
      if (!selectedSnapshot) return;

      const frequencyValue = Math.abs(Number(event.target.value));

      const newCurrentMarkers = [...currentMarkers];

      if (!newCurrentMarkers[index]) {
        newCurrentMarkers[index] = {
          snapshot_id: selectedSnapshotId,
          index: index,
          x_pos: 0,
          x_pos_frequency_value: '1500',
          x_pos_frequency_unit: 'MHz',
        };
      }

      const currentUnit = freqUnit(selectedSnapshot?.center);
      const unit =
        newCurrentMarkers[index]?.x_pos_frequency_unit || currentUnit;

      const frequency = `${frequencyValue} ${unit}`;

      const xPos = getIndexFromSnapshotFrequency(
        selectedSnapshot.center,
        selectedSnapshot.span,
        data?.length || 601,
        frequency,
        selectedSnapshot.lo,
        selectedSnapshot.lo_mode
      );

      const updatedMarker = {
        ...newCurrentMarkers[index],
        x_pos: xPos,
        x_pos_frequency_value:
          event.target.value.length > 0 ? frequencyValue : '',
      };

      newCurrentMarkers[index] = updatedMarker;
      setHasChanges(true);
      setCurrentMarkers(newCurrentMarkers);
      debouncedMarkerMoved(updatedMarker);
    };

    const handleMarkerUnitChanged = (event: any, index: MarkerIndex) => {
      if (!selectedSnapshot) return;
      const frequencyUnit = event.target.value;

      const newCurrentMarkers = [...currentMarkers];

      if (!newCurrentMarkers[index]) {
        newCurrentMarkers[index] = {
          snapshot_id: selectedSnapshotId,
          index: index,
          x_pos: 0,
          x_pos_frequency_value: '1500',
          x_pos_frequency_unit: 'MHz',
        };
      }

      const centerWithLo = getCenterWithLo(
        selectedSnapshot.center,
        selectedSnapshot.lo,
        selectedSnapshot.lo_mode
      );

      const frequencyValue =
        newCurrentMarkers[index]?.x_pos_frequency_value || centerWithLo;
      const oldFrequencyUnit =
        newCurrentMarkers[index]?.x_pos_frequency_unit || 'MHz';
      const oldFrequency = `${frequencyValue} ${oldFrequencyUnit}`;

      const adjustedFrequency = convertFrequency(oldFrequency, frequencyUnit);

      const updatedMarker = {
        ...newCurrentMarkers[index],
        x_pos_frequency_value: adjustedFrequency.split(' ')[0],
        x_pos_frequency_unit: frequencyUnit,
      };

      newCurrentMarkers[index] = updatedMarker;
      setCurrentMarkers(newCurrentMarkers);
      setHasChanges(true);
    };

    const handleSubmit = async () => {
      if (isHistoricalMode) {
        for (let i = 0; i < currentMarkerDeltas.length; i++) {
          if (!currentMarkerDeltas[i]) continue;
          if (!currentMarkerDeltas[i]?.id) {
            currentMarkerDeltas[i].id = uuid();
          }
        }

        dispatch(
          setTemporaryMarkerDeltas({
            snapshotId: selectedSnapshotId,
            snapshotMarkerDeltas: currentMarkerDeltas,
          })
        );

        return;
      }

      for (const [key] of Object.entries(currentMarkers)) {
        const marker = currentMarkers[Number(key)];

        const {
          x_pos_frequency_value,
          x_pos_frequency_unit,
          ...markerNoXPosExtras
        } = marker;

        await createMarkerMutation.mutateAsync({
          data: markerNoXPosExtras,
          snapshotId: markerNoXPosExtras.snapshot_id,
        });
      }

      if (trendlineMode) dispatch(trendlineModeToggled(false));

      handleClose();
      setHasChanges(false);

      for (let i = 0; i < currentMarkerDeltas.length; i++) {
        if (!currentMarkerDeltas[i]) continue;
        await insertSnapshotMarkerDelta(
          selectedSnapshotId,
          currentMarkerDeltas[i]
        );
      }
    };

    const handleCancelClicked = () => {
      setHasChanges(false);
      dispatch(clearClientsideMarkers());
      queryClient.invalidateQueries(['snapshot', selectedSnapshotId]);
      handleClose();
    };

    const handleMoveMarkerToCenter = async () => {
      if (!selectedSnapshot || !user || !selectedAnalyzer) return;
      if (focusedMarkerInput === null) return;

      const centerXPos = (data.length - 1) / 2;
      const currentMarker = currentMarkers[focusedMarkerInput];

      const newCenter = getCenterWithoutLo(
        snapshotFrequencyArray[currentMarker.x_pos],
        selectedSnapshot.lo
      );

      const updatedMarker = {
        id: currentMarker.id,
        index: focusedMarkerInput,
        snapshot_id: selectedSnapshotId,
        x_pos: centerXPos,
      };

      await dispatch(
        updateAnalyzerProperties({
          user_id: user.id,
          properties: {
            ...selectedSnapshot,
            center: newCenter,
          },
          analyzer: selectedAnalyzer,
        })
      );

      setHasChanges(true);
      dispatch(markerMoved(updatedMarker));

      await updateSnapshotMutation.mutateAsync({
        data: {
          ...selectedSnapshot,
          center: newCenter,
        },
        snapshotId: selectedSnapshot.id,
      });
    };

    const handleMoveMarkerToPeak = () => {
      if (focusedMarkerInput === null) return;

      data =
        isHistoricalMode && 'trace' in historicalSnapshot
          ? parseTraceFromBuffer(historicalSnapshot.trace)
          : data;
      const peakAmpIndex = data.indexOf(Math.max(...data));
      const currentMarkerId = currentMarkers[focusedMarkerInput]?.id
        ? currentMarkers[focusedMarkerInput].id
        : null;

      const updatedMarker = {
        id: currentMarkerId,
        index: focusedMarkerInput,
        snapshot_id: selectedSnapshotId,
        x_pos: peakAmpIndex,
      };

      setHasChanges(true);
      dispatch(markerMoved(updatedMarker));
      dispatch(selectedMarkerChanged(-1));
      setFocusedMarkerInput(null); //Check with Micky! Do we want to stop drawing mode?
    };

    const handleClearAllMarkers = async () => {
      setCurrentMarkers([]);
      setMarkerAmplitudes([]);
      setCurrentMarkerDeltas([]);

      if (isHistoricalMode) {
        dispatch(clearClientsideMarkers());
      } else {
        if (trendlineMode) {
          await clearTrendlineMarkers.mutateAsync({
            snapshotId: selectedSnapshotId,
          });
        } else {
          await clearMarkersMutation.mutateAsync({
            snapshotId: selectedSnapshotId,
          });
          await clearSnapshotMarkerDeltas(selectedSnapshotId);
        }
        dispatch(clearClientsideMarkers());
      }
    };

    useEffect(() => {
      return () => {
        dispatch(trendlineModeToggled(false));
      };
    }, [dispatch]);

    return (
      <Grid container direction='column' spacing={1}>
        {!isViewer && !isSnapshotLocked && (
          <Grid container item spacing={1}>
            <Grid item xs={3} style={{ minWidth: '5rem' }}>
              <Button
                onClick={handleCancelClicked}
                color='secondary'
                variant='contained'
                fullWidth
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={3} style={{ minWidth: '7rem' }}>
              <Button
                onClick={handleClearAllMarkers}
                color='error'
                variant='contained'
                fullWidth
              >
                Clear All
              </Button>
            </Grid>
            {!isHistoricalMode && (
              <Grid item xs={3} style={{ minWidth: '9rem' }}>
                <Button
                  onClick={handleSubmit}
                  color='primary'
                  variant='contained'
                  fullWidth
                  disabled={!hasChanges}
                >
                  Save Markers
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        <Grid container item spacing={2}>
          {!isHistoricalMode && !isViewer && !isSnapshotLocked && (
            <Grid item xs={3} style={{ minWidth: '5rem' }}>
              <Button
                onClick={handleMoveMarkerToCenter}
                color='primary'
                variant='contained'
                disabled={
                  focusedMarkerInput === null ||
                  !currentMarkers[focusedMarkerInput]?.x_pos ||
                  currentMarkers[focusedMarkerInput].x_pos === -1
                }
                fullWidth
              >
                Center
              </Button>
            </Grid>
          )}
          {!isViewer && !isSnapshotLocked && (
            <Grid item lg={3}>
              <Button
                onClick={handleMoveMarkerToPeak}
                color='primary'
                variant='contained'
                disabled={enablePeak()}
                fullWidth
              >
                Peak
              </Button>
            </Grid>
          )}
          {!isViewer && !isSnapshotLocked && !isHistoricalMode && (
            <Grid item lg={3}>
              <Button
                onClick={() => dispatch(trendlineModeToggled(!trendlineMode))}
                color='primary'
                variant='contained'
                fullWidth
              >
                {trendlineMode ? 'Markers' : 'Trendline'}
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            {trendlineMode ? (
              <TrendlineMarkerConfigurationInputs
                markers={currentMarkers}
                markerAmplitudes={markerAmplitudes}
                handleMarkerInputClicked={handleMarkerInputClicked}
                handleMarkerValueChanged={handleMarkerValueChanged}
                handleMarkerUnitChanged={handleMarkerUnitChanged}
                handleMarkerNameChanged={handleMarkerNameChanged}
                disabled={isViewer || (isSnapshotLocked && !isHistoricalMode)}
              />
            ) : (
              <MarkerConfigurationInputs
                markers={currentMarkers}
                markerAmplitudes={markerAmplitudes}
                handleMarkerInputClicked={handleMarkerInputClicked}
                handleMarkerValueChanged={handleMarkerValueChanged}
                handleMarkerUnitChanged={handleMarkerUnitChanged}
                disabled={isViewer || (isSnapshotLocked && !isHistoricalMode)}
              />
            )}
          </Grid>
          <Grid container item spacing={1}>
            {!trendlineMode && (
              <MarkerDeltaDisplay
                markers={currentMarkers}
                data={
                  isHistoricalMode && 'trace' in historicalSnapshot
                    ? parseTraceFromBuffer(historicalSnapshot.trace)
                    : data
                }
                markerDeltaAlarms={currentMarkerDeltas}
                handleMarkerDeltasChanged={handleMarkerDeltaChanged}
                disabled={isViewer || isSnapshotLocked}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default MarkerConfigurationDialog;
